import { evaluationService } from '@services/api';
import { useQuery } from '@tanstack/react-query';

import { METADATA_ASYNC_MIN_QUERY_LENGTH, type MetadataFilterWidget } from './types';

const EMPTY_VALUES: string[] = [];

const queryKeys = {
  metadataValues: {
    all: ['compass', 'metadataValues'] as const,
    byKey: (
      organizationId: string,
      key: string,
      params: {
        q?: string;
        programIds?: string[];
        dateFrom?: string;
        dateTo?: string;
      },
    ) =>
      [
        ...queryKeys.metadataValues.all,
        'byKey',
        organizationId,
        key,
        params.q ?? '',
        [...(params.programIds ?? [])].sort(),
        params.dateFrom ?? '',
        params.dateTo ?? '',
      ] as const,
  },
};

interface UseMetadataValuesParams {
  organizationId?: string | null;
  /** metadata key (slugified/registry form) */
  key?: string;
  widget: MetadataFilterWidget;
  /** Static option list from the registry entry; bypasses the network call. */
  staticOptions?: string[];
  programIds?: string[];
  /** Call-start window as UTC ISO strings (already timezone-resolved). */
  callStartTimeAfter?: string;
  callStartTimeBefore?: string;
  /** Live typed search (async-search-only widget); ignored otherwise. */
  q?: string;
}

/**
 * Returns the option values for one metadata advanced filter (AQ-496).
 *
 * - Static options (registry `ui_control.options`) short-circuit the network call.
 * - `async-search-only` fetches WITH `q` and is only enabled once q ≥ 2 chars —
 *   the value endpoint then does a case-insensitive prefix search
 *   (high-cardinality keys like Skill).
 * - Every other widget fetches the distinct value list once (no `q`).
 */
// Async keys only query once the user has typed the minimum; distinct keys
// fetch eagerly. Static-option keys never query.
const shouldQueryMetadataValues = (args: {
  hasStaticOptions: boolean;
  organizationId?: string | null;
  key?: string;
  isAsync: boolean;
  hasEnoughQuery: boolean;
}): boolean => {
  if (args.hasStaticOptions || !args.organizationId || !args.key) {
    return false;
  }
  return !args.isAsync || args.hasEnoughQuery;
};

// Comma-joined, matching useAgentsList/useAgentsSummary: the query param is
// read server-side via firstQueryValue + strings.Split(v, ","), so an array
// serialized as repeated program_ids=A&program_ids=B params (per the
// dashboard's paramsSerializer `indexes: null`) would silently drop all but
// the first program. The generated request type is `string[]` (it mirrors the
// repeated proto field), but the wire contract here is a single CSV string —
// the same gap useAgentsSummary/useAgentsList work around with a local
// request type; a cast is used here instead since this call goes straight
// through the generated service.
const joinProgramIdsForWire = (programIds?: string[]): string[] | undefined =>
  programIds && programIds.length > 0 ? (programIds.join(',') as unknown as string[]) : undefined;

/** Derived, non-hook flags for one useMetadataValues call — split out purely to
 * keep the hook body's own complexity under the lint limit. */
function deriveMetadataValuesQueryState(args: {
  staticOptions?: string[];
  organizationId?: string | null;
  key?: string;
  widget: MetadataFilterWidget;
  q?: string;
}) {
  const hasStaticOptions = !!args.staticOptions && args.staticOptions.length > 0;
  const isAsync = args.widget === 'async-search-only';
  const trimmedQuery = (args.q ?? '').trim();
  const hasEnoughQuery = trimmedQuery.length >= METADATA_ASYNC_MIN_QUERY_LENGTH;
  const enabled = shouldQueryMetadataValues({
    hasStaticOptions,
    organizationId: args.organizationId,
    key: args.key,
    isAsync,
    hasEnoughQuery,
  });
  return {
    hasStaticOptions,
    isAsync,
    hasEnoughQuery,
    enabled,
    effectiveQuery: isAsync ? trimmedQuery : undefined,
  };
}

export const useMetadataValues = ({
  organizationId,
  key,
  widget,
  staticOptions,
  programIds,
  callStartTimeAfter,
  callStartTimeBefore,
  q,
}: UseMetadataValuesParams) => {
  const { hasStaticOptions, isAsync, hasEnoughQuery, enabled, effectiveQuery } =
    deriveMetadataValuesQueryState({ staticOptions, organizationId, key, widget, q });

  const query = useQuery({
    queryKey: queryKeys.metadataValues.byKey(organizationId || '', key || '', {
      q: effectiveQuery,
      programIds,
      dateFrom: callStartTimeAfter,
      dateTo: callStartTimeBefore,
    }),
    queryFn: async () => {
      if (!organizationId || !key) {
        return EMPTY_VALUES;
      }

      const response = await evaluationService.getEvaluationMetadataValues({
        organization_id: organizationId,
        key,
        q: effectiveQuery || undefined,
        program_ids: joinProgramIdsForWire(programIds),
        call_start_time_after: callStartTimeAfter,
        call_start_time_before: callStartTimeBefore,
      });

      return response.values ?? EMPTY_VALUES;
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  if (hasStaticOptions && staticOptions) {
    return {
      values: staticOptions,
      isLoading: false,
      isFetching: false,
    };
  }

  // React Query keeps the last successful `data` around when a query becomes
  // disabled (it doesn't clear on enabled:false) — for the async-search widget
  // that means backspacing below the 2-char minimum would otherwise keep
  // showing the previous query's results instead of the "type more" empty
  // state. Suppress stale values in exactly that case.
  const suppressStaleAsyncResults = isAsync && !hasEnoughQuery;

  return {
    values: suppressStaleAsyncResults ? EMPTY_VALUES : (query.data ?? EMPTY_VALUES),
    isLoading: query.isLoading && enabled,
    isFetching: query.isFetching,
  };
};
